import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { ActionItemDto, ApprovalState, Client, ReportType, ResolutionAndMitigationPlanDto } from '@fdm/api-client';
import { MaterialModule } from '@fdm/material';
import { AuthService, Role } from '@fdm/services';
import { filterTable, getErrorMessage, noWhitespaceValidator } from '@fdm/utils';
import { ToastrService } from 'ngx-toastr';
import {
  catchError,
  combineLatest,
  concatMap,
  EMPTY,
  filter,
  from,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { ApprovalFlowService, FormValidationService, NavigationService, ReportFormPage, ReportsBulkSaveService, ReportTask, ResolutionAndMitigationApiService } from '../../../../services';
import { PolicyService } from '../../../../services/roles/policy.service';
import { ReportForm } from '../../report-form';
import { AngularEditorModule, AngularEditorConfig } from '@kolkov/angular-editor';
import { createEditorConfigs } from '../../shared/richtext-editor-config-helper';
import { StripHtmlPipe } from '../../../../../shared/pipes/strip-html.pipe';
import { FilterResponse, TableColumnFilterComponent } from '@fdm/shared';

@Component({
  selector: 'app-mitigation-resolution-tab',
  standalone: true,
  imports: [
    CommonModule, 
    ReactiveFormsModule, 
    MaterialModule, 
    AngularEditorModule, 
    StripHtmlPipe,
    
    // Components
    TableColumnFilterComponent
  ],
  providers: [ResolutionAndMitigationApiService],
  templateUrl: './mitigation-resolution-tab.component.html',
  styleUrl: './mitigation-resolution-tab.component.scss',
})
export class MitigationResolutionTabComponent extends ReportForm implements OnInit, AfterViewInit {
  @ViewChild(MatSort) sort!: MatSort;
  @Input() data: any = {};
  @Input() readonly: boolean = false;

  canEdit$ = this._policy.canEditTable$;

  private editorConfigs = createEditorConfigs(7, 'mitigation');
  configMitigationEn: AngularEditorConfig = this.editorConfigs[0];
  configMitigationRu: AngularEditorConfig = this.editorConfigs[1];
  configMitigationKz: AngularEditorConfig = this.editorConfigs[2];
  configResolutionEn: AngularEditorConfig = this.editorConfigs[3];
  configResolutionRu: AngularEditorConfig = this.editorConfigs[4];
  configResolutionKz: AngularEditorConfig = this.editorConfigs[5];
  configValidation: AngularEditorConfig = this.editorConfigs[6];

  public form!: FormGroup;

  displayedColumns: string[] = ['select', 'actionItemId', 'actionItemDescriptionEn', 'actionItemOwner', 'dueDate', 'status', 'completedOn'];
  dataSource: MatTableDataSource<ActionItemDto>;
  selection = new SelectionModel<ActionItemDto>(true, []);

  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private _navigationService = inject(NavigationService);
  private apiClient = inject(Client);
  private api = inject(ResolutionAndMitigationApiService);
  private destroyRef = inject(DestroyRef);
  private toastrService = inject(ToastrService);
  private _bulkSave = inject(ReportsBulkSaveService);
  private _policyService = inject(PolicyService);
  private _formValidationService = inject(FormValidationService);
  private _approvalFlow = inject(ApprovalFlowService);

  private _cdr = inject(ChangeDetectorRef);

  private _authService = inject(AuthService);

  isAdmin = signal(this._authService.isInRole(Role.Admin));

  private _general = toSignal(this._store.general$);
  private _closeReport = toSignal(this._store.closeReport$);
  private currentStep = toSignal(this._store.page$);
  private _mitigationResolution = toSignal(this._store.mitigationResolution$);

  private generalTabId!: string;
  private mitigationTabId!: number;

  public resolutionTargetDate = computed(() => {
    const general = this._general();

    return general?.resolutionTargetDate;
  });

  private currentState = computed(() => this._general()?.currentState);
  private reportType = computed(() => this._general()?.reportType);

  public get step() {
    return ReportFormPage.MITIGATION_AND_RESOLUTION_PLAN;
  }

  private get component() {
    return 'MitigationResolutionTabComponent';
  }

  // public get aiId() {
  //   return this.form?.get('id') as FormControl<number | null>;
  // }

  constructor() {
    super();
    this.dataSource = new MatTableDataSource([] as ActionItemDto[]);

    effect(() => {
      const state = this.currentState();
      const validatonVerificationCommentControl = this.form.get('validatonVerificationComment');
      if (
        (this.currentState() === ApprovalState.ResolutionPlanCompleted ||
          this.currentState() === ApprovalState.ValidationAndVerificationCompleted ||
          this.currentState() === ApprovalState.Closed) && this.reportType() !== ReportType.TaskDeferral
      ) {
        validatonVerificationCommentControl?.setValidators([Validators.required, Validators.maxLength(20000), noWhitespaceValidator()]);
      } else {
        validatonVerificationCommentControl?.setValidators([Validators.maxLength(20000)]);
      }
      validatonVerificationCommentControl?.updateValueAndValidity({ emitEvent: false });
    });

    this._cdr.markForCheck(); // Trigger change detection

  }

  override ngOnInit(): void {
    this.initializeForm();
    super.ngOnInit();
    this._initTriggers();
  }

  protected override onPolicyOrFormStateChanged() {
    this.richTextFieldPolicy();
  }
  richTextFieldPolicy() {
    this.configMitigationEn.editable = this.isFieldEditable('mitigationPlanEn');
    this.configMitigationRu.editable = this.isFieldEditable('mitigationPlanRu');
    this.configMitigationKz.editable = this.isFieldEditable('mitigationPlanKz');
    this.configResolutionEn.editable = this.isFieldEditable('resolutionPlanEn');
    this.configResolutionRu.editable = this.isFieldEditable('resolutionPlanRu');
    this.configResolutionKz.editable = this.isFieldEditable('resolutionPlanKz');
    this.configValidation.editable = this.isFieldEditable('validatonVerificationComment');
  }

  isRequired(fieldName: string): boolean {
    const control = this.form.get(fieldName);

    if (!control) {
      return false;
    }

    if (control.validator && !control.disabled) {
      const validator = control.validator({} as AbstractControl);
      if (validator && validator['required']) {
        return true;
      }
    }
    return false;
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
  }

  displayVerificationField() {
    return (this.currentState() === ApprovalState.ResolutionPlanCompleted ||
      this.currentState() === ApprovalState.ValidationAndVerificationCompleted ||
      this.currentState() === ApprovalState.Closed) && this.reportType() !== ReportType.TaskDeferral;
  }

  addNewActionItemButton() {

    const isITRorRTR = this.reportType() === ReportType.IntegrityThreat || this.reportType() === ReportType.ReliabilityThreat;
    const isIntegrityDeficiencyorReliabilityDeficiencyDeferral = this.reportType() === ReportType.IntegrityDeficiency || this.reportType() === ReportType.ReliabilityDeficiency || this.reportType() === ReportType.TaskDeferral;

    const isAnomalyReport = this.reportType() === ReportType.AnomalyReport;
    const isDraftStateForAnomaly = this.currentState() === ApprovalState.Draft || this.currentState() === ApprovalState.DraftedForReview;

    const isDraftStateForDeficienciesDeferral = this.currentState() === ApprovalState.Draft || this.currentState() === ApprovalState.DraftedForReview || this.currentState() === ApprovalState.SubmittedToOpsAdvisor || this.currentState() === ApprovalState.RiskEvaluationScored;
    const isDraftStateForThreat = this.currentState() === ApprovalState.Draft || this.currentState() === ApprovalState.DraftedForReview || this.currentState() === ApprovalState.SubmittedToOpsAdvisor;


    if (isITRorRTR) {
      return isDraftStateForThreat ? true : false;
    }
    else if (isIntegrityDeficiencyorReliabilityDeficiencyDeferral) {
      return isDraftStateForDeficienciesDeferral ? true : false;
    }
    else if (isAnomalyReport) {
      return isDraftStateForAnomaly ? true : false;
    }
    else {
      return false;
    }

  }
  initializeForm(): void {
    this.form = this.fb.group({
      id: [null],
      mitigationPlanEn: ['', [Validators.maxLength(20000)]],
      mitigationPlanRu: ['', [Validators.maxLength(20000)]],
      mitigationPlanKz: ['', [Validators.maxLength(20000)]],
      resolutionPlanEn: ['', [Validators.required, Validators.maxLength(20000), noWhitespaceValidator()]],
      resolutionPlanRu: ['', [Validators.maxLength(20000)]],
      resolutionPlanKz: ['', [Validators.maxLength(20000)]],
      validatonVerificationComment: ['', [Validators.maxLength(20000)]],
      aiItems: this.fb.array([]),
    });

    this._formValidationService.registerForm(ReportFormPage.MITIGATION_AND_RESOLUTION_PLAN, this.form);
  }

  displayNames: { [key: string]: string } = {
    mitigationPlanEn: 'Mitigation Plan [EN]',
    mitigationPlanRu: 'Mitigation Plan [RU]',
    mitigationPlanKz: 'Mitigation Plan [KZ]',
    resolutionPlanEn: 'Resolution Plan [EN]',
    resolutionPlanRu: 'Resolution Plan [RU]',
    resolutionPlanKz: 'Resolution Plan [KZ]',
    validatonVerificationComment: 'Validation and Verification Comments',
  };

  getErrorMessage(fieldName: string): string | null {
    return getErrorMessage(
      this.form,
      fieldName,
      this.displayNames
    );
  }

  private _save() {
    if (this.form.invalid) {
      // this.form.markAllAsTouched();
      // this.toastrService.error('Please fill all required fields in the form.');
      return of(null);
    }

    const formValues = this.form.value;

    const payload = new ResolutionAndMitigationPlanDto({
      ...formValues,
      mitigationPlanEn: formValues.mitigationPlanEn ?? undefined,
      mitigationPlanRu: formValues.mitigationPlanRu ?? undefined,
      mitigationPlanKz: formValues.mitigationPlanKz ?? undefined,
      resolutionPlanEn: formValues.resolutionPlanEn ?? undefined,
      resolutionPlanRu: formValues.resolutionPlanRu ?? undefined,
      resolutionPlanKz: formValues.resolutionPlanKz ?? undefined,
      validatonVerificationComment: formValues.validatonVerificationComment ?? undefined,
      id: this.mitigationTabId ?? undefined,
      generalTabId: this.generalTabId,
    });

    const request$ = this.mitigationTabId ? this.api.update(payload) : this.api.save(payload);

    return request$
      .pipe(
        switchMap((res) => {
          this._store.reset();

          if (!this.mitigationTabId && res) {
            this.mitigationTabId = res;
          }

          // hide notification for closing
          if (this._general()?.currentState !== ApprovalState.ValidationAndVerificationCompleted) {
            this.toastrService.success(`Mitigation and Resolution Plan ${this.mitigationTabId ? 'updated' : 'saved'} successfully.`);
          }

          if (this._closeReport()) {
            this._navigationService.navigateToListPage();
            return of(null);
          }

          return this._getMitigationResolution();
        }),
        catchError((error) => {
          this._store.reset();

          this.toastrService.error(`Failed to ${this.mitigationTabId ? 'update' : 'save'} Mitigation and Resolution Plan.`);

          console.warn(`[Error][${this.component}}]:`, error);

          // TODO: temporary solution, need to revert after status code checking (200 -> 201)
          // return this._getMitigationResolution();
          return of(error);
        }),
      );
  }

  private _initTriggers(): void {

    this._approvalFlow.flowEventsUpdated
      .pipe(
        switchMap(() => this._getMitigationResolution()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this._store.page$
      .pipe(
        filter((page) => page === this.step),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this._store.setPageValidity({ page: this.step, isStepValid: this.form.valid });
      });

    this.form.valueChanges
      .pipe(
        filter(() => this.step === this.currentStep()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        const isFormValid = this.form.valid;

        if (isFormValid) {
          const payload: ReportTask = {
            page: this.step,
            request: this._save()
          };

          if (this._bulkSave.includes(this.step)) {
            this._bulkSave.update(payload);
          } else {
            this._bulkSave.add(payload);
          }
        }

        this._store.setPageValidity({ page: this.step, isStepValid: isFormValid });
      });

    combineLatest([this.route.paramMap, this.route.queryParamMap])
      .pipe(
        // filter(([_, query]) => query.get('step') === this.step),
        filter(() => !this.form.dirty),
        switchMap(([params, _]) => {
          const id = params.get('id');
          if (!id) {
            return EMPTY;
          }
          this.generalTabId = id;

          const storedData = this._mitigationResolution();

          if (storedData && storedData.generalTabId === id) {
            this.form.patchValue(storedData, { emitEvent: false });

            if (storedData.actionItems != undefined) {
              this.dataSource.data = [...storedData.actionItems] as ActionItemDto[];
              this.tempTableData = [...this.dataSource.data];

              this._updateAIdata(this.dataSource.data);

              if (storedData.id) {
                this.mitigationTabId = storedData.id;
              }
            }

            return EMPTY;
          }

          return this._getMitigationResolution();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private _updateAIdata(values: ActionItemDto[]) {
    const arrayControl = this.form.get('aiItems') as FormArray<any>;

    arrayControl.clear({ emitEvent: false });

    values.forEach(item => {
      arrayControl.push(this.fb.group(ActionItemDto.fromJS(item)), { emitEvent: false })
    });
  }

  private _getMitigationResolution() {
    return this.generalTabId
      ? this.apiClient.byGeneralTab4(this.generalTabId).pipe(
        tap((data) => {
          if (data) {
            if (data.id) {
              this.form.get('aiItems')?.addValidators(Validators.required);
              this.form.get('aiItems')?.updateValueAndValidity();
            }

            this.form.patchValue(data, { emitEvent: false });
            this._store.setMitigationResolution(data);

            if (data.actionItems != undefined) {
              this.dataSource.data = [...data.actionItems] as ActionItemDto[];
              this.tempTableData = [...this.dataSource.data];
              this._updateAIdata(this.dataSource.data);

              if (data.id) {
                this.mitigationTabId = data.id;
              }
            }
          }
        }),
        catchError((err) => {
          if (err.status === 404) {
            return of(null);
          }

          this.toastrService.error('Failed to load Mitigation and Resolution Plan.');
          return of(null);
        })
      )
      : of(null);
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.dataSource.data.forEach((row) => this.selection.select(row));
  }

  addActionItem() {
    const queue = this._bulkSave.queue();

    if (queue.length) {
      this._bulkSave.save();

      this._bulkSave.saveComplete
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this._navigationService.navigateToActionItemCreate({
            generalTabId: this.generalTabId,
            resolutionAndMitigationPlanId: this.mitigationTabId,
          });
        });
    } else {
      this._navigationService.navigateToActionItemCreate({
        generalTabId: this.generalTabId,
        resolutionAndMitigationPlanId: this.mitigationTabId,
      });
    }
  }


  editActionItem(id: number) {
    this._navigationService.navigateToActionItemEdit(id, {
      generalTabId: this.generalTabId,
      resolutionAndMitigationPlanId: this.mitigationTabId,
    });
  }

  removeActionItem() {
    const actionItemIds = this.selection.selected.map((item) => item.id);
    const actionItemId$ = from(actionItemIds).pipe(
      filter((id): id is number => id != null),
      concatMap((id) => this.apiClient.actionItemDELETE(id).pipe(map(() => id)))
    );

    actionItemId$.subscribe({
      next: (deletedId) => {
        this.dataSource.data = this.dataSource.data.filter(
          (item) => item.id !== deletedId
        );
        this.dataSource.data = [...this.dataSource.data];
        this.tempTableData = [...this.dataSource.data];
      },
      error: (error) => {
        console.error(error);
        this.toastrService.error('Failed to delete action item.');
      },
      complete: () => {
        this.selection.clear();
      },
    });
  }

  filters: Record<string, FilterResponse | null> = {};
  tempTableData: any[] = [];

  applyFilter(res: FilterResponse | null, column: string) {
    this.filters[column] = res;
    this.dataSource.data = filterTable(this.tempTableData, column, res);
  }

  isActive(column: string) {    
    return this.filters[column] != null;
  }
}
